// @flow

import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import type { HierarchyTree } from "@modules/shared/reducer/hierarchy/types";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";
import Step1Rule from "./step1-rule";
import Step2Assignments from "./step2-assignments";
import type { State } from "@store/reducers";
import type { EmployeeSmsRule } from "@modules/service/reducer/employee-sms-notifications/types";
import type { HierarchyAssignment } from "@modules/service/types";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  current: ?EmployeeSmsRule,
  assignments: Array<HierarchyAssignment>,
  hierarchy: ?HierarchyTree,
  isAssignmentsValid: boolean,
}>;

type PropsFromDispatch = $Exact<{
  setCurrent: typeof actionCreators.SetEmployeeSmsCurrent,
  setAssignments: typeof actionCreators.SetEmployeeSmsAssignments,
  add: typeof actionCreators.AddEmployeeSmsRule,
  setShowModal: typeof actionCreators.SetShowAddEmployeeSmsModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EmployeeSmsAddModal extends Component<Props> {
  validate: *;

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validate = new Ajv().compile(this.props.schema);
    }
  }

  get isValid() {
    return !this.validate || this.validate(this.props.current);
  }

  handleCancel = () => {
    this.props.setShowModal(false);
  };

  handleSubmit = () => {
    this.props.add({
      clientId: this.props.clientId,
      rule: this.props.current,
      assignments: this.props.assignments,
    });
  };

  handleChange = ([step1, step2]: Array<Object> = []) => {
    this.props.setCurrent(step1);

    if (step2) {
      this.props.setAssignments(step2.assignments);
    }
  };

  get assignmentsAreValid() {
    if (!this.props.assignments) return false;
    return !this.props.assignments.some((assignment: Object) => assignment.activeFrom === null);
  }

  get stepData() {
    return [
      {
        data: {
          id: 0,
        },
        valid: false,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("EMPLOYEE_SMS_MODAL_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          {this.props.schema ? (
            <LinearWizard
              defaultStep={1}
              data={this.stepData}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
            >
              <Step
                component={Step1Rule}
                title={this.props.t("EMPLOYEE_SMS_MODAL_STEP1_TITLE")}
                schema={this.props.schema}
              />
              <Step
                component={Step2Assignments}
                title={this.props.t("EMPLOYEE_SMS_MODAL_STEP2_TITLE")}
                hierarchy={this.props.hierarchy}
                assignments={this.props.assignments}
              />
            </LinearWizard>
          ) : null}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button
            color="secondary"
            onClick={this.handleCancel}
            disabled={!this.props.isAssignmentsValid}
          >
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="add_box"
            onClick={this.handleSubmit}
            disabled={!this.isValid || !this.assignmentsAreValid}
          >
            {this.props.t("ADD")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    schema: serviceSelectors.getEmployeeSmsSchema(state),
    current: serviceSelectors.getEmployeeSmsCurrent(state),
    assignments: serviceSelectors.getEmployeeSmsAssignments(state),
    hierarchy: sharedSelectors.getHierarchyTree(state),
    isAssignmentsValid: serviceSelectors.isEmployeeSmsAssignmentsValid(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setCurrent: actionCreators.SetEmployeeSmsCurrent,
  setAssignments: actionCreators.SetEmployeeSmsAssignments,
  add: actionCreators.AddEmployeeSmsRule,
  setShowModal: actionCreators.SetShowAddEmployeeSmsModal,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeSmsAddModal);
